import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import HealthIntakeFormLayout from '../components/health/HealthIntakeFormLayout'
import HealthDataConsentFields from '../components/health/HealthDataConsentFields'
import { useHealthDataConsentBundle } from '../hooks/useHealthDataConsentBundle'
import {
  HEALTH_DATA_CONSENT_VARIANTS,
  allHealthDataConsentsChecked,
  buildHealthDataConsentPayload,
  createInitialHealthDataConsents,
} from '../lib/healthDataConsent'

const CONSENT_VARIANT = 'health-analysis-customer'

// 비로그인 공개 페이지: 고객 직접 인증 정보 입력
export default function HealthAuthPage() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [jobInfo, setJobInfo] = useState(null)
  const [identity, setIdentity] = useState('')
  const [telecom, setTelecom] = useState('')
  const [authProvider, setAuthProvider] = useState('KAKAO')
  const [smsAuthNo, setSmsAuthNo] = useState('')
  const [submittingSmsAuth, setSubmittingSmsAuth] = useState(false)
  const [pollingStatus, setPollingStatus] = useState(false)
  const [smsRequested, setSmsRequested] = useState(false)
  const [requestingSms, setRequestingSms] = useState(false)
  const [dataConsents, setDataConsents] = useState({})

  const consentBundleQuery = useHealthDataConsentBundle(CONSENT_VARIANT, { publicApi: true })
  const consentBundle = consentBundleQuery.data
  const consentItems = consentBundle?.items ?? []

  useEffect(() => {
    if (consentItems.length) {
      setDataConsents(createInitialHealthDataConsents(consentItems))
    }
  }, [consentBundle?.variant, consentBundle?.bundleVersion, consentItems.length])

  useEffect(() => {
    let ignore = false
    ;(async () => {
      if (!token) return
      setLoading(true)
      setError('')
      try {
        const res = await api.get(`/public/health-jobs/auth/${token}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) {
          setJobInfo(payload)
          if (payload?.telecom) setTelecom(payload.telecom)
          if (payload?.authProvider) setAuthProvider(payload.authProvider)
          if (payload?.identitySubmitted === true) {
            setSubmitted(true)
          }
          if (payload?.status === 'AUTH_INPUT_REQUIRED' && payload?.authProvider === 'SMS') {
            setSmsRequested(true)
          }
        }
      } catch (e) {
        if (!ignore) {
          const status = e?.response?.status
          if (status === 404 || status === 410) {
            setError('링크가 만료되었거나 유효하지 않습니다.')
          } else {
            setError('정보를 불러오는데 실패했습니다.')
          }
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [token])

  useEffect(() => {
    if (!pollingStatus || !token) return

    let ignore = false
    let pollCount = 0
    const maxPolls = 60

    const poll = async () => {
      if (ignore || pollCount >= maxPolls) {
        if (pollCount >= maxPolls) setPollingStatus(false)
        return
      }
      pollCount++

      try {
        const res = await api.get(`/public/health-jobs/auth/${token}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) {
          setJobInfo(payload)
          if (payload?.status !== 'AUTH_INPUT_REQUIRED') {
            setPollingStatus(false)
            if (payload?.status === 'AUTH_WAITING' || payload?.status === 'ANALYSIS_RUNNING' || payload?.status === 'ANALYSIS_COMPLETED') {
              setSubmitted(true)
            }
          }
        }
      } catch {
        if (!ignore) setPollingStatus(false)
      }
    }

    poll()
    const intervalId = setInterval(poll, 2000)
    return () => {
      ignore = true
      clearInterval(intervalId)
    }
  }, [pollingStatus, token])

  const buildIdentityPayload = (identityDigits) => ({
    identity: identityDigits,
    telecom,
    authProvider,
    ...buildHealthDataConsentPayload(consentBundle, dataConsents),
  })

  const ensureConsents = () => {
    if (!allHealthDataConsentsChecked(consentItems, dataConsents)) {
      setError('필수 동의 항목을 모두 확인해 주세요.')
      return false
    }
    return true
  }

  const handleIdentityChange = (e) => {
    setIdentity(e.target.value.replaceAll(/[^0-9-]/g, ''))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!token) return

    if (authProvider === 'SMS' && smsRequested) {
      const smsAuthNoTrim = smsAuthNo.trim()
      if (!smsAuthNoTrim || smsAuthNoTrim.length < 4) {
        setError('인증번호를 입력해 주세요.')
        return
      }

      setSubmittingSmsAuth(true)
      setError('')
      try {
        await api.post(`/public/health-jobs/auth/${token}/sms-auth`, { smsAuthNo: smsAuthNoTrim })
        const jobRes = await api.get(`/public/health-jobs/auth/${token}`)
        const jobPayload = (jobRes?.data && jobRes.data.data !== undefined) ? jobRes.data.data : jobRes.data
        setJobInfo(jobPayload)

        if (jobPayload?.status === 'AUTH_WAITING' || jobPayload?.status === 'ANALYSIS_RUNNING') {
          setSubmitted(true)
          setPollingStatus(true)
        } else if (jobPayload?.status === 'AUTH_INPUT_REQUIRED') {
          setError('인증번호가 올바르지 않습니다. 다시 입력해 주세요.')
          setSmsAuthNo('')
        } else if (jobPayload?.status === 'FAILED') {
          setError(jobPayload?.lastError || '인증에 실패했습니다. 다시 시도해 주세요.')
          setSmsAuthNo('')
        }
      } catch (err) {
        const status = err?.response?.status
        if (status === 404 || status === 410) setError('링크가 만료되었거나 유효하지 않습니다.')
        else if (status === 409) setError('인증번호를 입력할 수 없는 상태입니다.')
        else setError('인증번호 제출에 실패했습니다. 잠시 후 다시 시도해 주세요.')
      } finally {
        setSubmittingSmsAuth(false)
      }
      return
    }

    const identityDigits = identity.replaceAll(/[^0-9]/g, '')
    if (identityDigits.length !== 13) {
      setError('주민등록번호는 13자리 숫자로 입력해 주세요.')
      return
    }
    if (!telecom) {
      setError('통신사를 선택해 주세요.')
      return
    }
    if (!ensureConsents()) return

    setSubmitting(true)
    setError('')
    try {
      await api.post(`/public/health-jobs/auth/${token}/identity`, buildIdentityPayload(identityDigits))

      const jobRes = await api.get(`/public/health-jobs/auth/${token}`)
      const jobPayload = (jobRes?.data && jobRes.data.data !== undefined) ? jobRes.data.data : jobRes.data
      setJobInfo(jobPayload)

      if (authProvider === 'SMS') {
        setSubmitted(false)
        if (jobPayload) {
          jobPayload.identitySubmitted = true
          setJobInfo({ ...jobPayload })
        }
      } else {
        setSubmitted(true)
      }
    } catch (err) {
      const status = err?.response?.status
      if (status === 404 || status === 410) setError('링크가 만료되었거나 유효하지 않습니다.')
      else if (status === 409) setError('이미 인증이 완료되었거나 진행 중입니다.')
      else if (status === 400) setError('필수 동의를 확인하거나 입력 정보를 다시 확인해 주세요.')
      else setError('인증 정보 제출에 실패했습니다. 잠시 후 다시 시도해 주세요.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRequestSms = async () => {
    if (!token || requestingSms) return

    const identityDigits = identity.replaceAll(/[^0-9]/g, '')
    if (identityDigits.length !== 13) {
      setError('주민등록번호는 13자리 숫자로 입력해 주세요.')
      return
    }
    if (!telecom) {
      setError('통신사를 선택해 주세요.')
      return
    }
    if (!ensureConsents()) return

    setRequestingSms(true)
    setError('')
    try {
      const identitySubmitted = jobInfo?.identitySubmitted === true
      if (!identitySubmitted) {
        await api.post(`/public/health-jobs/auth/${token}/identity`, buildIdentityPayload(identityDigits))
        const jobRes = await api.get(`/public/health-jobs/auth/${token}`)
        const jobPayload = (jobRes?.data && jobRes.data.data !== undefined) ? jobRes.data.data : jobRes.data
        setJobInfo(jobPayload)
      }

      const res = await api.post(`/public/health-jobs/auth/${token}/request-sms`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data

      if (payload?.smsSent === true) {
        setSmsRequested(true)
        const jobRes = await api.get(`/public/health-jobs/auth/${token}`)
        const jobPayload = (jobRes?.data && jobRes.data.data !== undefined) ? jobRes.data.data : jobRes.data
        setJobInfo(jobPayload)
      } else {
        setError(payload?.error || 'SMS 인증번호 발송에 실패했습니다.')
      }
    } catch (err) {
      const status = err?.response?.status
      if (status === 404 || status === 410) setError('링크가 만료되었거나 유효하지 않습니다.')
      else if (status === 409) {
        const errorMsg = err?.response?.data?.message || err?.response?.data?.error || ''
        if (errorMsg.includes('SMS_ALREADY_SENT') || errorMsg.includes('이미 SMS')) {
          setError('이미 SMS 인증번호가 발송되었습니다.')
          setSmsRequested(true)
        } else {
          setError('이미 인증이 완료되었거나 진행 중입니다.')
        }
      } else if (status === 400) {
        setError('필수 동의를 확인하거나 입력 정보를 다시 확인해 주세요.')
      } else {
        setError('SMS 인증번호 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.')
      }
    } finally {
      setRequestingSms(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-600">불러오는 중...</div>
      </div>
    )
  }

  if (error && !jobInfo) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="mx-4 w-full max-w-md">
          <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {error}
          </div>
        </div>
      </div>
    )
  }

  const customerName = jobInfo?.customerName || '고객'
  const phoneNumber = jobInfo?.phoneNumber || ''
  const jobStatus = jobInfo?.status
  const jobAuthProvider = jobInfo?.authProvider || authProvider
  const identitySubmitted = jobInfo?.identitySubmitted === true
  const needsSmsAuthInput = (jobStatus === 'AUTH_INPUT_REQUIRED' || smsRequested) && (jobAuthProvider === 'SMS' || authProvider === 'SMS')
  const canSubmitSmsAuth = needsSmsAuthInput && smsAuthNo.trim().length >= 4
  const formDisabled = submitting || submittingSmsAuth || identitySubmitted

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto max-w-5xl px-4">
        <div className="mb-4">
          <h1 className="text-xl font-bold text-slate-900">진료정보 분석 인증</h1>
          <p className="mt-1 text-sm text-slate-600">
            {customerName}님, 본인확인 후 진료정보 분석이 진행됩니다.
          </p>
        </div>

        {submitted && !pollingStatus ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            {jobStatus === 'AUTH_WAITING' || jobStatus === 'ANALYSIS_RUNNING'
              ? '인증이 진행 중입니다. 잠시만 기다려 주세요.'
              : jobStatus === 'ANALYSIS_COMPLETED'
                ? '인증이 완료되었습니다. 분석이 진행됩니다.'
                : '인증 정보가 제출되었습니다. 담당자가 확인 후 분석을 진행하겠습니다.'}
          </div>
        ) : (
          <HealthIntakeFormLayout
            serviceLabel={HEALTH_DATA_CONSENT_VARIANTS[CONSENT_VARIANT].serviceLabel}
            onSubmit={handleSubmit}
            identityPanel={(
              <div className="space-y-3 text-sm">
                <div>
                  <label className="mb-1 block font-medium text-slate-700">이름</label>
                  <input
                    type="text"
                    className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600"
                    value={customerName}
                    disabled
                    readOnly
                  />
                </div>
                <div>
                  <label className="mb-1 block font-medium text-slate-700">전화번호</label>
                  <input
                    type="text"
                    className="w-full rounded border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600"
                    value={phoneNumber}
                    disabled
                    readOnly
                  />
                </div>
                <div>
                  <label className="mb-1 block font-medium text-slate-700">
                    주민등록번호 <span className="text-rose-600">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full rounded border border-slate-300 px-3 py-2 focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy"
                    value={identity}
                    onChange={handleIdentityChange}
                    maxLength={14}
                    disabled={formDisabled}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block font-medium text-slate-700">통신사</label>
                  <select
                    className="w-full rounded border border-slate-300 px-3 py-2 focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy"
                    value={telecom}
                    onChange={(e) => setTelecom(e.target.value)}
                    disabled={formDisabled}
                  >
                    <option value="">선택해 주세요</option>
                    <option value="0">SKT</option>
                    <option value="1">KT</option>
                    <option value="2">LG U+</option>
                    <option value="3">알뜰폰(SKT)</option>
                    <option value="4">알뜰폰(KT)</option>
                    <option value="5">알뜰폰(LG U+)</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block font-medium text-slate-700">
                    인증 수단 <span className="text-rose-600">*</span>
                  </label>
                  <select
                    className="w-full rounded border border-slate-300 px-3 py-2 focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy"
                    value={authProvider}
                    onChange={(e) => setAuthProvider(e.target.value)}
                    disabled={formDisabled}
                    required
                  >
                    <option value="KAKAO">카카오톡</option>
                    <option value="SMS">SMS</option>
                  </select>
                </div>

                {authProvider === 'SMS' && (
                  <div className="space-y-3">
                    {!smsRequested ? (
                      <button
                        type="button"
                        onClick={handleRequestSms}
                        className="w-full rounded border border-brand-navy bg-white px-4 py-2 text-sm font-medium text-brand-navy hover:bg-slate-50 disabled:opacity-50"
                        disabled={requestingSms || submittingSmsAuth || !identity.trim() || !telecom || consentBundleQuery.isLoading || !allHealthDataConsentsChecked(consentItems, dataConsents)}
                      >
                        {requestingSms ? '발송 중...' : 'SMS 인증번호 받기'}
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <div className="rounded border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
                          SMS 인증번호가 발송되었습니다.
                        </div>
                        <div>
                          <label className="mb-1 block font-medium text-slate-700">
                            SMS 인증번호 <span className="text-rose-600">*</span>
                          </label>
                          <input
                            type="text"
                            className="w-full rounded border border-slate-300 px-3 py-2 focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy"
                            placeholder="인증번호 6자리"
                            value={smsAuthNo}
                            onChange={(e) => setSmsAuthNo(e.target.value.replaceAll(/[^0-9]/g, ''))}
                            maxLength={6}
                            disabled={submittingSmsAuth}
                            autoFocus
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            consentPanel={(
              <HealthDataConsentFields
                items={consentItems}
                checked={dataConsents}
                onChange={setDataConsents}
                disabled={formDisabled}
                loading={consentBundleQuery.isLoading}
              />
            )}
            footerActions={(
              <>
                {error ? (
                  <div className="w-full rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                    {error}
                  </div>
                ) : null}
                <button
                  type="submit"
                  className="rounded bg-brand-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-sky disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={
                    submitting
                    || submittingSmsAuth
                    || consentBundleQuery.isLoading
                    || !allHealthDataConsentsChecked(consentItems, dataConsents)
                    || (authProvider === 'SMS' && (!smsRequested || !canSubmitSmsAuth))
                    || (authProvider !== 'SMS' && (!identity.trim() || !telecom))
                  }
                >
                  {submitting || submittingSmsAuth ? '제출 중...' : (authProvider === 'SMS' && smsRequested ? '인증번호 제출' : '인증 정보 제출')}
                </button>
              </>
            )}
          />
        )}
      </div>
    </div>
  )
}
